import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Container } from 'components/base';
import {
  CloudinaryPicture,
  ScriptMarkupBreadcrumb,
  ScriptMarkupFaq,
  WIDTH_LADDERS,
  getCloudinaryDprSrcSet,
  getCloudinaryUrl,
} from 'components/rich';

import Breadcrumb from '../components/breadcrumb';
import { Responsive, ctaTracker, productImage } from '../components/landing/shared';
import useActiveSection from '../components/landing/useActiveSection';
import { Head, Layout } from '../components/layout';
import PdpIcon from '../components/product/pdpIcon';
import ProductFaq from '../components/product/productFaq';
import SETTINGS from '../settings';

// Year in Photos guide (docs/year-in-photos.md). One evergreen story page: the same year as a
// photo book, Square Prints or an Engineer Prints photo grid. Copy follows the Pencil frames
// "Year in Photos — Redesign" / "— Mobile". Prices and delivery dates stay on product pages.
const track = ctaTracker('year_in_photos');

const ID = {
  FORMATS: 'formats',
  BOOK: 'photo-book',
  PRINTS: 'square-prints',
  GRID: 'photo-grid',
  CHOOSING: 'choosing-photos',
  FAQ: 'faq',
};

const HREF = {
  BOOK: '/photo-books',
  PRINTS: '/square-prints',
  // Multi-photo grids. /square-engineer-prints is a different, single-photo product.
  GRID: '/engineer-prints',
};

const blogImage = (key) => ({ imageKey: `blogpost/${key}` });
const instagramImage = (key) => ({ imageKey: `instagram/${key}`, widths: WIDTH_LADDERS.instagram });
const landingImage = (key) => ({ imageKey: `landing/${key}`, widths: WIDTH_LADDERS.landing });

const IMAGES = {
  wallGrid: blogImage('spring'),
  bookOpen: productImage('premium-hardcover-books/08'),
  squarePile: instagramImage('instagram2'),
  formatBook: productImage('instagram-yearbook/3'),
  printsInHand: productImage('square-prints/2'),
  printsOnWall: productImage('square-prints/3'),
  printsStack: productImage('square-prints/8'),
  bookInHands: landingImage('photo-books/hc-hands-open'),
  bookCaptions: landingImage('photo-books/sc-captions-open'),
  bookFullPage: productImage('hardcover-book/2'),
  grid30Room: landingImage('year-in-photos/engineer-print-30-grid-living-room'),
  grid130Detail: landingImage('year-in-photos/engineer-print-130-grid-detail'),
};

// Without `widths` the folder has no ladder (blogpost/), so the DPR pair is used.
const Photo = ({ image, sizes, alt = '', priority }) => (
  <CloudinaryPicture
    imageKey={image.imageKey}
    prefix={image.prefix}
    widths={image.widths}
    sizes={image.widths ? sizes : undefined}
    alt={alt}
    priority={priority}
    loading={priority ? undefined : 'lazy'}
  />
);

Photo.propTypes = {
  image: PropTypes.shape({
    imageKey: PropTypes.string.isRequired,
    prefix: PropTypes.string,
    widths: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  sizes: PropTypes.string.isRequired,
  alt: PropTypes.string,
  priority: PropTypes.bool,
};

const Pill = ({ children, className }) => (
  <span className={classNames('yip-pill', className)}>{children}</span>
);

Pill.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

const ArrowButton = ({ href, children, onClick, className }) => (
  <Link href={href} className={classNames('lnd-btn', className)} onClick={onClick}>
    {children}
    <PdpIcon name="arrowRight" size={18} />
  </Link>
);

ArrowButton.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

const HERO_FACTS = [
  { icon: 'smartphone', label: 'Phone or computer' },
  { icon: 'flag', label: 'Printed in the USA' },
  { icon: 'leaf', label: 'FSC-certified paper' },
];

const MOMENTS = [
  { month: 'February', line: 'A new face at the table', image: productImage('square-prints/2') },
  {
    month: 'April',
    line: 'The trip we talked about for years',
    phone: 'The trip we talked about',
    image: productImage('square-prints/6'),
  },
  { month: 'July', line: 'Sand, salt, no plans', image: productImage('premium-hardcover-books/06') },
  {
    month: 'Any Tuesday',
    line: 'The dog, every single day',
    phone: 'The dog, every day',
    image: productImage('hardcover-book/2'),
  },
  {
    month: 'October',
    line: 'Pizza night with everyone',
    image: productImage('premium-hardcover-books/08'),
    desktopOnly: true,
  },
  {
    month: 'December',
    line: 'All of us, home',
    image: instagramImage('instagram2'),
    desktopOnly: true,
  },
];

const FORMATS = [
  {
    id: ID.BOOK,
    product: 'Photo book',
    title: 'A year to turn through',
    line: 'Pages you sit down with, from the first trip to the last dinner, in the order you choose.',
    phone: 'Pages you sit down with, in the order you choose.',
    link: 'See the photo book',
    image: IMAGES.formatBook,
  },
  {
    id: ID.PRINTS,
    product: 'Square Prints',
    title: 'Favorite moments to hold',
    line: 'One photo per print, to pass around, prop on a shelf, or hang in a row.',
    phone: 'One photo per print, to pass around or hang in a row.',
    link: 'See square prints',
    image: IMAGES.printsInHand,
  },
  {
    id: ID.GRID,
    product: 'Photo grid poster · Engineer Prints',
    phoneProduct: 'Photo grid · Engineer Prints',
    title: 'A whole year on your wall',
    line: 'Up to 130 photos on one 3x4 ft print, so the year reads at a glance.',
    phone: 'Up to 130 photos on one 3x4 ft print.',
    link: 'See the photo grid',
    image: IMAGES.wallGrid,
  },
];

const CONTENTS = {
  'By month': [
    ['January', 2],
    ['February', 6],
    ['March', 10],
    ['April', 14],
  ],
  'By moment': [
    ['Snow day', 2],
    ['Spring trip', 8],
    ['Grandma’s 80th', 16],
    ['Summer', 22],
  ],
};

const COVERS = [
  {
    name: 'Softcover',
    why: 'Light and easy to pass around',
    specs: ['6x6 or 8x8 in square', '26 pages included, up to 100', '1 to 3 photos per page'],
    phone: '6x6 or 8x8 in · 26 to 100 pages',
    link: 'Softcover Photo Books',
    url: '/softcover-books',
    image: productImage('softcover-books/1'),
  },
  {
    name: 'Hardcover',
    why: 'Sturdy, built to flip for years',
    specs: ['8x8 in square', '26 pages included, up to 100', '1 to 3 photos per page'],
    phone: '8x8 in · 26 to 100 pages',
    link: '8x8 Hardcover Book',
    url: '/hardcover-book',
    image: productImage('hardcover-book/1'),
  },
  {
    name: 'Premium Hardcover',
    why: 'For a big year with lots to say',
    specs: [
      '11x8.5 or 13x10 in landscape',
      '20 pages included, up to 350',
      'Edit every page, layout and text',
    ],
    phone: 'Landscape · 20 to 350 pages',
    link: 'Premium Hardcover',
    url: '/premium-hardcover-books',
    image: productImage('premium-hardcover-books/10'),
  },
];

const MONTH_LETTERS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
// A set of Square Prints is 20, so the last two months are the 4 extra prints.
const SET_SIZE = 20;

const PRINT_FACTS = [
  { icon: 'layers', label: 'Sets of 20, add more' },
  { icon: 'ruler', label: '2.75, 4 or 5.5 in' },
  { icon: 'frame', label: 'Border or edge to edge' },
];

// Schematic grid sheets, not the exact template geometry. Colors cycle forest, ochre, terracotta.
const GRID_LAYOUTS = [
  { label: '9 photos', columns: 3, rows: 3 },
  { label: '30 photos', columns: 5, rows: 6 },
  { label: '130 photos', columns: 10, rows: 13 },
];
const GRID_PATTERN = [0, 1, 0, 2, 0, 0, 1, 0, 0, 0, 2, 1, 0];

const STEPS = [
  {
    title: 'Make one album for the year',
    body: 'In your phone’s Photos app, start an album for this year and scroll it month by month.',
    phone: 'Scroll it month by month in your phone’s Photos app.',
  },
  {
    title: 'Pick a few from each month',
    body: 'A person, an outing, an ordinary moment. Keep the best of near-duplicates and let the rest go.',
    phone: 'A person, an outing, an ordinary moment. Drop near-duplicates.',
  },
  {
    title: 'Add what you never posted',
    body: 'The photos that never made it to Instagram are often the ones you’ll want most.',
    phone: 'Those are often the ones you’ll want most.',
  },
  {
    title: 'Then match it to the format',
    body: 'Narrow it down for prints and a grid, or keep more for a book.',
    phone: 'Narrow it down for prints and a grid, keep more for a book.',
  },
];

const STARTING_POINTS = [
  { count: '24', format: 'Square Prints', detail: '2 a month. A set is 20, add 4 extra prints.' },
  { count: '30', format: 'Grid of 30', detail: '24 monthly moments plus 6 favorite details.' },
  {
    count: '36–72',
    format: 'Photo book',
    detail: '3 to 6 a month. Settle the page count once the layout is done.',
    phone: '3 to 6 a month. Settle the page count after the layout.',
  },
];

// Shape ProductFaq renders. `description` is HTML, the FAQPage JSON-LD gets it without tags.
const FAQ = [
  {
    caption: 'Can I use photos from my phone and the ones I posted on Instagram?',
    description:
      'Yes. Upload them from your phone or computer like any other photo. For posts, use the original file from your camera roll. A screenshot of a post has fewer pixels and prints less sharp.',
  },
  {
    caption: 'How many photos do I need for each format?',
    description:
      'Square Prints come in sets of 20, and you can add extra prints. A photo grid takes 9, 30 or 130 photos. Softcover and Hardcover books start at 26 pages with 1 to 3 photos per page, so the page count depends on your layout.',
  },
  {
    caption: 'How do cropping and layout work?',
    description:
      'You see every photo in its spot before you order and can adjust the crop. Grid spots all have the same shape, so check that faces and details are not cut off.',
  },
  {
    caption: 'Can I add my own text to a book?',
    description:
      'Yes. Add a title on the cover and captions on the pages. You type the text yourself. Nothing is copied over from Instagram captions.',
  },
  {
    caption: 'Where can I see the current price, production and delivery?',
    description:
      'Each product page shows the current price and production time. Delivery times and holiday order-by dates are on our <a href="/shipping">Shipping page</a>.',
  },
];

const FAQ_JSON_LD = FAQ.map(({ caption, description }) => ({
  question: caption,
  answer: description.replace(/<[^>]+>/g, ''),
}));

const FINAL_PATHS = [
  {
    product: 'Photo book',
    line: 'Softcover, Hardcover or Premium',
    cta: 'Make a photo book',
    url: HREF.BOOK,
    image: IMAGES.formatBook,
  },
  {
    product: 'Square Prints',
    line: 'Sets of 20, add more',
    cta: 'Print your favorites',
    url: HREF.PRINTS,
    image: IMAGES.printsStack,
  },
  {
    product: 'Photo grid',
    line: 'Engineer Prints · 3x4 ft',
    cta: 'Create a photo grid',
    url: HREF.GRID,
    image: IMAGES.wallGrid,
  },
];

const NAV = [
  { id: ID.BOOK, label: 'Photo book' },
  { id: ID.PRINTS, label: 'Square prints' },
  { id: ID.GRID, label: 'Photo grid' },
  { id: ID.CHOOSING, label: 'Choosing photos' },
  { id: ID.FAQ, label: 'FAQ' },
];
const NAV_IDS = NAV.map(({ id }) => id);

// The nav button follows the active section. Choosing photos, FAQ and the top of the page fall
// back to the format picker.
const NAV_CTA = {
  [ID.BOOK]: { hint: 'Softcover, Hardcover or Premium', label: 'Make a photo book', href: HREF.BOOK },
  [ID.PRINTS]: { hint: 'Sets of 20, add more', label: 'Print your favorites', href: HREF.PRINTS },
  [ID.GRID]: { hint: 'Engineer Prints · 3x4 ft', label: 'Create a photo grid', href: HREF.GRID },
};
const NAV_CTA_FALLBACK = { label: 'Find your format', href: `#${ID.FORMATS}` };

const BREADCRUMB = [{ name: 'Home', url: '/' }, { name: 'Year in Photos' }];

// Desktop: underlined links + a CTA for the active section. Phones: chips that scroll sideways,
// the active chip is kept in view. Plain anchors, so the outline works without JS.
const SectionNav = () => {
  const active = useActiveSection(NAV_IDS);
  const listRef = useRef(null);
  const cta = NAV_CTA[active] ?? NAV_CTA_FALLBACK;

  useEffect(() => {
    const list = listRef.current;
    const chip = list?.querySelector('.active');
    if (!chip || list.scrollWidth <= list.clientWidth) return;
    // Scroll the chip strip only, scrollIntoView would also move the page.
    list.scrollTo({ left: chip.offsetLeft - 16, behavior: 'smooth' });
  }, [active]);

  return (
    <nav className="yip-nav" aria-label="On this page">
      <Container className="yip-nav-inner">
        <ul className="yip-nav-list" ref={listRef}>
          {NAV.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={classNames('yip-nav-link', { active: active === item.id })}
                aria-current={active === item.id ? 'location' : undefined}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="yip-nav-cta">
          {cta.hint && <p className="yip-nav-hint">{cta.hint}</p>}
          <a
            href={cta.href}
            className="lnd-btn yip-btn-small"
            onClick={track(cta.href === NAV_CTA_FALLBACK.href ? 'find_format' : 'product', 'nav', cta.href)}
          >
            {cta.label}
          </a>
        </div>
      </Container>
    </nav>
  );
};

const Hero = () => (
  <section className="lnd-hero yip-hero">
    <Container className="yip-hero-inner">
      <div className="yip-hero-copy">
        <span className="lnd-desk">
          <Breadcrumb items={BREADCRUMB} />
        </span>
        <h1 className="lnd-h1">Your year in photos, made to keep</h1>
        <p className="lnd-lead">
          Make a year in review photo book, a set of square prints, or a wall grid from the moments
          you want to remember.
        </p>
        <div className="lnd-hero-actions yip-hero-actions">
          <a href={`#${ID.FORMATS}`} className="lnd-btn" onClick={track('find_format', 'hero')}>
            Find your format
            <PdpIcon name="arrowDown" size={18} />
          </a>
          <a
            href={`#${ID.CHOOSING}`}
            className="yip-text-link lnd-desk"
            onClick={track('choosing_photos', 'hero')}
          >
            Not sure where to start?
          </a>
        </div>
        <ul className="lnd-facts yip-hero-facts lnd-desk">
          {HERO_FACTS.map((fact) => (
            <li key={fact.label}>
              <PdpIcon name={fact.icon} size={16} />
              {fact.label}
            </li>
          ))}
        </ul>
      </div>

      <div className="yip-scene">
        <figure className="yip-scene-grid">
          <Photo
            image={IMAGES.wallGrid}
            sizes="(min-width: 992px) 570px, 100vw"
            alt="A 3x4 ft Engineer Print photo grid hanging on a living room wall"
            priority
          />
          <figcaption>
            <Pill>Wall grid · 3x4 ft</Pill>
          </figcaption>
        </figure>
        <figure className="yip-scene-book">
          <Photo
            image={IMAGES.bookOpen}
            sizes="(min-width: 992px) 330px, 50vw"
            alt="An open Premium Hardcover photo book"
          />
          <figcaption>
            <Pill>Photo book</Pill>
          </figcaption>
        </figure>
        <figure className="yip-scene-prints">
          <Photo
            image={IMAGES.squarePile}
            sizes="(min-width: 992px) 280px, 50vw"
            alt="A pile of Square Prints"
          />
          <figcaption>
            <Pill>Square prints</Pill>
          </figcaption>
        </figure>
      </div>
    </Container>
  </section>
);

const Story = () => (
  <section className="yip-story">
    <Container>
      <div className="yip-story-copy">
        <p className="yip-statement">
          The big days. The ordinary Tuesdays. The people and places that made this year yours.
        </p>
        <p className="yip-story-text">
          <Responsive
            desktop="Most of a year happens between the milestones. Dinner at the usual table, a walk with the dog, the friend who finally visited. Those are the photos that stay on your phone. Printed, they become the year you actually lived."
            phone="Most of a year happens between the milestones. Printed, those moments become the year you actually lived."
          />
        </p>
      </div>
      <ul className="yip-moments">
        {MOMENTS.map((moment) => (
          <li
            key={moment.month}
            className={classNames('yip-moment', { 'lnd-desk': moment.desktopOnly })}
          >
            <div className="yip-moment-photo">
              <Photo image={moment.image} sizes="(min-width: 992px) 184px, 150px" />
            </div>
            <p className="yip-moment-month">{moment.month}</p>
            <p className="yip-moment-line">
              <Responsive desktop={moment.line} phone={moment.phone} />
            </p>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

const Formats = () => (
  <section id={ID.FORMATS} className="yip-section yip-cream yip-formats">
    <Container>
      <div className="yip-head">
        <div>
          <p className="yip-eyebrow">Find your format</p>
          <h2 className="yip-h2">How do you want to remember this year?</h2>
        </div>
        <p className="yip-head-aside lnd-desk">
          The same moments work in all three. Pick the one that fits how you’ll look at them.
        </p>
      </div>
      <ul className="yip-format-list">
        {FORMATS.map((format) => (
          <li key={format.id}>
            <a
              href={`#${format.id}`}
              className="yip-format"
              onClick={track('format', 'formats', format.id)}
            >
              <div className="yip-format-photo">
                <Photo image={format.image} sizes="(min-width: 992px) 379px, 104px" />
              </div>
              <div className="yip-format-text">
                <p className="yip-product-label">
                  <Responsive desktop={format.product} phone={format.phoneProduct} />
                </p>
                <h3 className="yip-format-title">{format.title}</h3>
                <p className="yip-format-line">
                  <Responsive desktop={format.line} phone={format.phone} />
                </p>
                <span className="yip-arrow-link lnd-desk">
                  {format.link}
                  <PdpIcon name="arrowDown" size={16} />
                </span>
              </div>
              <PdpIcon name="chevronDown" size={20} className="yip-format-chevron lnd-phone" />
            </a>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

const ContentsPage = () => (
  <div className="yip-contents" aria-label="Example contents page">
    <p className="yip-contents-title">Contents</p>
    <div className="yip-contents-columns">
      {Object.entries(CONTENTS).map(([label, entries]) => (
        <div key={label}>
          <p className="yip-contents-label">{label}</p>
          <ul>
            {entries.map(([entry, page]) => (
              <li key={entry}>
                <span>{entry}</span>
                <span className="yip-contents-page">{page}</span>
              </li>
            ))}
            <li aria-hidden="true">…</li>
          </ul>
        </div>
      ))}
    </div>
  </div>
);

const PhotoBook = () => (
  <section id={ID.BOOK} className="yip-section yip-book">
    <Container>
      <div className="yip-book-intro">
        <div className="yip-book-copy">
          <p className="yip-eyebrow">
            <Responsive desktop="Photo book · A year to turn through" phone="Photo book" />
          </p>
          <h2 className="yip-h2 yip-h2--large">Make a year in review photo book</h2>
          <p className="yip-support">
            Give your year a place on the shelf. Gather the trips, everyday favorites, and
            in-between moments into a photo book you can come back to.
          </p>
          <div className="yip-actions lnd-desk">
            <ArrowButton href={HREF.BOOK} onClick={track('product', 'photo_book', HREF.BOOK)}>
              Make a photo book
            </ArrowButton>
            <span className="yip-note">Sizes and prices on Photo Books</span>
          </div>
        </div>
        <div className="yip-book-photo">
          <Photo
            image={IMAGES.bookInHands}
            sizes="(min-width: 992px) 668px, 100vw"
            alt="Hands holding an open hardcover photo book"
          />
        </div>
      </div>

      <ul className="yip-inside">
        <li className="yip-inside-item">
          <div className="yip-inside-visual">
            <ContentsPage />
          </div>
          <div>
            <h3 className="yip-h3">Chapters by month or by moment</h3>
            <p className="yip-body">
              <Responsive
                desktop="Go through the year in order, or group it around the trips and days that mattered. You place every page, so the order is yours."
                phone="Go through the year in order, or group it around the trips and days that mattered. You place every page."
              />
            </p>
          </div>
        </li>
        <li className="yip-inside-item">
          <div className="yip-inside-visual yip-layout-pair">
            <figure className="lnd-desk">
              <Photo image={IMAGES.bookFullPage} sizes="186px" />
              <figcaption>
                <Pill>One photo, full page</Pill>
              </figcaption>
            </figure>
            <figure>
              <Photo image={IMAGES.bookOpen} sizes="(min-width: 768px) 186px, 76px" />
              <figcaption className="lnd-desk">
                <Pill>A few photos together</Pill>
              </figcaption>
            </figure>
          </div>
          <div>
            <h3 className="yip-h3">One big photo or a few together</h3>
            <p className="yip-body">
              <Responsive
                desktop="Give a favorite a full page and group the smaller moments, up to 3 photos on a page in Softcover and Hardcover books."
                phone="A favorite gets a full page, smaller moments share one, up to 3 per page in Softcover and Hardcover."
              />
            </p>
          </div>
        </li>
        <li className="yip-inside-item">
          <div className="yip-inside-visual">
            <Photo image={IMAGES.bookCaptions} sizes="(min-width: 992px) 379px, 76px" />
          </div>
          <div>
            <h3 className="yip-h3">A title and a few words</h3>
            <p className="yip-body">
              <Responsive
                desktop="Put the year on the cover and add short captions where they help. You type them yourself, so they say what you want."
                phone="Put the year on the cover and add short captions. You type them yourself."
              />
            </p>
          </div>
        </li>
      </ul>

      <div className="yip-covers">
        <div className="yip-covers-head">
          <h3 className="yip-covers-title">Pick a cover</h3>
          <p className="yip-pages-note lnd-desk">
            <PdpIcon name="info" size={16} />
            Pages are not photos. Choose your photos first, then let the layout set the page count.
          </p>
        </div>
        <ul className="yip-cover-list">
          {COVERS.map((cover) => (
            <li key={cover.name}>
              <Link
                href={cover.url}
                className="yip-cover"
                onClick={track('cover', 'photo_book', cover.url)}
              >
                <div className="yip-cover-thumb">
                  <Photo image={cover.image} sizes="(min-width: 768px) 96px, 52px" />
                </div>
                <div className="yip-cover-text">
                  <p className="yip-cover-name">{cover.name}</p>
                  <p className="yip-cover-why lnd-desk">{cover.why}</p>
                  <ul className="yip-cover-specs lnd-desk">
                    {cover.specs.map((spec) => (
                      <li key={spec}>{spec}</li>
                    ))}
                  </ul>
                  <p className="yip-cover-specs lnd-phone">{cover.phone}</p>
                  <span className="yip-arrow-link yip-arrow-link--small lnd-desk">
                    {cover.link}
                    <PdpIcon name="arrowRight" size={15} />
                  </span>
                </div>
                <PdpIcon name="chevronRight" size={18} className="yip-cover-chevron lnd-phone" />
              </Link>
            </li>
          ))}
        </ul>
        <p className="yip-pages-note lnd-phone">
          Pages are not photos. Choose your photos first, then let the layout set the page count.
        </p>
      </div>

      <ArrowButton
        href={HREF.BOOK}
        className="yip-phone-cta lnd-phone"
        onClick={track('product', 'photo_book', HREF.BOOK)}
      >
        Make a photo book
      </ArrowButton>
    </Container>
  </section>
);

const TwoAMonth = () => (
  <div className="yip-two-a-month">
    <p className="yip-two-title">Two from every month = 24 prints</p>
    <ol className="yip-months" aria-label="24 prints, two for each month">
      {MONTH_LETTERS.map((letter, index) => (
        <li key={`${letter}-${index}`}>
          {[0, 1].map((slot) => (
            <span
              key={slot}
              className={classNames('yip-month-print', {
                'yip-month-print--extra': index * 2 + slot >= SET_SIZE,
              })}
            />
          ))}
          <span className="yip-month-letter" aria-hidden="true">
            {letter}
          </span>
        </li>
      ))}
    </ol>
    <ul className="yip-legend lnd-desk">
      <li>
        <span className="yip-month-print" />A set of 20
      </li>
      <li>
        <span className="yip-month-print yip-month-print--extra" />4 extra prints, added one by one
      </li>
    </ul>
    <p className="yip-legend-text lnd-phone">A set is 20. Add the 4 extra prints one by one.</p>
  </div>
);

const SquarePrints = () => (
  <section id={ID.PRINTS} className="yip-section yip-cream yip-prints">
    <Container className="yip-prints-inner">
      <div className="yip-prints-copy">
        <p className="yip-eyebrow">
          <Responsive desktop="Square Prints · Favorite moments to hold" phone="Square Prints" />
        </p>
        <h2 className="yip-h2 yip-h2--large">Your favorite moments, one print at a time</h2>
        <p className="yip-support">
          <Responsive
            desktop="No book to plan, no pages to fill. Pick the moments one by one and print each on thick matte paper. Hand them out, lean them on a shelf, or line up the whole year on a wall."
            phone="No book to plan. Pick the moments one by one and print each on thick matte paper."
          />
        </p>
      </div>

      <div className="yip-prints-photos">
        <div className="yip-prints-hand">
          <Photo
            image={IMAGES.printsInHand}
            sizes="(min-width: 992px) 420px, 60vw"
            alt="Hands holding a stack of Square Prints"
          />
        </div>
        <figure className="yip-prints-wall">
          <Photo
            image={IMAGES.printsOnWall}
            sizes="(min-width: 992px) 300px, 130px"
            alt="A set of Square Prints hung in a grid on a wall"
          />
          <figcaption>
            <Pill>
              <Responsive desktop="A set of 20 on the wall" phone="A set of 20" />
            </Pill>
          </figcaption>
        </figure>
      </div>

      <div className="yip-prints-details">
        <TwoAMonth />
        <ul className="yip-facts lnd-desk">
          {PRINT_FACTS.map((fact) => (
            <li key={fact.label}>
              <PdpIcon name={fact.icon} size={16} />
              {fact.label}
            </li>
          ))}
        </ul>
        <div className="yip-actions">
          <ArrowButton href={HREF.PRINTS} onClick={track('product', 'square_prints', HREF.PRINTS)}>
            Print your favorites
          </ArrowButton>
          <span className="yip-note lnd-desk">Price per set on Square Prints</span>
        </div>
      </div>
    </Container>
  </section>
);

const GridSheet = ({ columns, rows }) => (
  <span
    className={classNames('yip-sheet', { 'yip-sheet--dense': columns > 5 })}
    style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
    aria-hidden="true"
  >
    {Array.from({ length: columns * rows }, (_, index) => (
      <span key={index} className={`yip-sheet-photo--${GRID_PATTERN[index % GRID_PATTERN.length]}`} />
    ))}
  </span>
);

GridSheet.propTypes = {
  columns: PropTypes.number.isRequired,
  rows: PropTypes.number.isRequired,
};

const PhotoGrid = () => (
  <section id={ID.GRID} className="yip-section yip-grid">
    <Container>
      <div className="yip-head yip-head--wide">
        <div>
          <p className="yip-eyebrow">
            <Responsive
              desktop="Photo grid poster · Engineer Prints"
              phone="Photo grid · Engineer Prints"
            />
          </p>
          <h2 className="yip-h2 yip-h2--large">See your whole year at once</h2>
        </div>
        <p className="yip-support yip-grid-support">
          <Responsive
            desktop="A printed photo grid on one 3x4 ft Engineer Print. Pick a layout for 9, 30 or 130 photos and the year fills a wall, light enough to hang from a single nail."
            phone="A printed photo grid on one 3x4 ft Engineer Print, with room for 9, 30 or 130 photos."
          />
        </p>
      </div>

      <div className="yip-grid-photos">
        <figure className="yip-grid-main">
          <span className="lnd-desk">
            <Photo
              image={IMAGES.wallGrid}
              sizes="800px"
              alt="A 130 photo Engineer Print grid hanging from Wood Poster Rails in a living room"
            />
          </span>
          <span className="lnd-phone">
            <Photo
              image={IMAGES.grid130Detail}
              sizes="100vw"
              alt="A 3x4 foot Engineer Print with a grid of 130 photos hangs from black Poster Rails in a living room."
            />
          </span>
          <figcaption>
            <Pill className="yip-pill--icon">
              <PdpIcon name="ruler" size={15} />3 x 4 ft · 130 photos
            </Pill>
            <Pill className="yip-pill--dark">Shown with Wood Poster Rails, sold separately</Pill>
          </figcaption>
        </figure>
        <div className="yip-grid-side lnd-desk">
          <figure>
            <Photo
              image={IMAGES.grid30Room}
              sizes="380px"
              alt="A 3x4 foot Engineer Print with a grid of 30 photos hangs from wood Poster Rails next to a fireplace and a rocking chair."
            />
            <figcaption>
              <Pill>30 photos</Pill>
            </figcaption>
          </figure>
          <figure>
            <Photo
              image={IMAGES.grid130Detail}
              sizes="380px"
              alt="A 3x4 foot Engineer Print with a grid of 130 photos hangs from black Poster Rails in a living room."
            />
            <figcaption>
              <Pill>Up close, 130 photos</Pill>
            </figcaption>
          </figure>
        </div>
      </div>

      <div className="yip-grid-options">
        <ul className="yip-layouts">
          {GRID_LAYOUTS.map((layout) => (
            <li key={layout.label}>
              <GridSheet columns={layout.columns} rows={layout.rows} />
              <span className="yip-layout-label">{layout.label}</span>
            </li>
          ))}
        </ul>
        <div className="yip-tip">
          <p className="yip-tip-title">Check the small squares</p>
          <p className="yip-body">
            <Responsive
              desktop="Every spot in a grid is cropped to the same shape. The more photos, the smaller each one prints, so close-ups read better than wide group shots in the 130 layout. Look at each crop before you order."
              phone="Every spot is cropped to the same shape. In the 130 layout, close-ups read better than wide group shots."
            />
          </p>
        </div>
        <div className="yip-grid-cta">
          <ArrowButton href={HREF.GRID} onClick={track('product', 'photo_grid', HREF.GRID)}>
            Create a photo grid
          </ArrowButton>
          <span className="yip-note lnd-desk">Opens Engineer Prints</span>
        </div>
      </div>
    </Container>
  </section>
);

const ChoosingPhotos = () => (
  <section id={ID.CHOOSING} className="yip-section yip-mint">
    <Container className="yip-choosing">
      <div className="yip-steps-column">
        <p className="yip-eyebrow">Choosing your photos</p>
        <h2 className="yip-h2 yip-h2--large">Start with one month</h2>
        <p className="yip-support">
          <Responsive
            desktop="You don’t have to sort a whole camera roll or tidy up a whole year. Pick a month, then the next one."
            phone="You don’t have to sort a whole camera roll. Pick a month, then the next one."
          />
        </p>
        <ol className="yip-steps">
          {STEPS.map((step, index) => (
            <li key={step.title}>
              <span className="yip-step-number" aria-hidden="true">
                {index + 1}
              </span>
              <div>
                <h3 className="yip-step-title">{step.title}</h3>
                <p className="yip-body">
                  <Responsive desktop={step.body} phone={step.phone} />
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="yip-points-column">
        <div className="yip-points">
          <h3 className="yip-points-title">How many photos to start with</h3>
          <p className="yip-points-sub">Starting points, not limits</p>
          <dl>
            {STARTING_POINTS.map((point) => (
              <div key={point.format} className="yip-point">
                <dt className="yip-point-count">{point.count}</dt>
                <dd>
                  <span className="yip-point-format">{point.format}</span>
                  <span className="yip-point-detail">
                    <Responsive desktop={point.detail} phone={point.phone} />
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="yip-instagram">
          <PdpIcon name="instagram" size={22} />
          <div>
            <h3 className="yip-instagram-title">Posted it on Instagram?</h3>
            <p className="yip-body">
              <Responsive
                desktop="Your posts are a good shortlist of the year. To print, upload the original photo from your phone or computer. A screenshot of a post is small and prints soft."
                phone="Your posts are a good shortlist. To print, upload the original photo from your phone. A screenshot of a post prints soft."
              />
            </p>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

// Shared PDP FAQ pattern, first question open.
const Faq = () => (
  <section id={ID.FAQ} className="yip-section yip-faq">
    <Container>
      <ProductFaq
        eyebrow="Before you start"
        title="Questions about your year in print"
        intro={false}
        items={FAQ}
      />
    </Container>
  </section>
);

const FinalChoice = () => (
  <section className="yip-section yip-cream yip-final">
    <Container>
      <div className="yip-final-head">
        <h2 className="yip-h2">Keep this year the way you’ll use it</h2>
        <p className="yip-final-sub lnd-desk">Same moments, three ways to have them around.</p>
      </div>
      <ul className="yip-paths">
        {FINAL_PATHS.map((path) => (
          <li key={path.product} className="yip-path">
            <div className="yip-path-top">
              <div className="yip-path-thumb">
                <Photo image={path.image} sizes="96px" />
              </div>
              <div>
                <p className="yip-path-product">{path.product}</p>
                <p className="yip-path-line">{path.line}</p>
              </div>
            </div>
            <ArrowButton
              href={path.url}
              className="yip-btn-small yip-path-cta"
              onClick={track('product', 'final', path.url)}
            >
              {path.cta}
            </ArrowButton>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

const YearInPhotosPage = () => (
  <>
    <Head
      title="Year in Review Photo Books, Prints & Posters"
      description="Turn your favorite moments into a year in review photo book, square prints, or a wall grid. Find a format and simple ideas for choosing your photos."
      image={getCloudinaryUrl({ imageKey: 'premium-hardcover-books/08', prefix: 'products/', format: 'jpg' })}
      heroImage={getCloudinaryUrl(IMAGES.wallGrid)}
      heroImageSrcSet={getCloudinaryDprSrcSet(IMAGES.wallGrid)}
    />
    <ScriptMarkupBreadcrumb items={BREADCRUMB} host={SETTINGS.HOST} />
    <ScriptMarkupFaq items={FAQ_JSON_LD} />

    <Layout>
      <div className="lnd yip">
        <Hero />
        <SectionNav />
        <Story />
        <Formats />
        <PhotoBook />
        <SquarePrints />
        <PhotoGrid />
        <ChoosingPhotos />
        <Faq />
        <FinalChoice />
      </div>
    </Layout>
  </>
);

export default YearInPhotosPage;
